import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { FileTextIcon, HeadingIcon, MoveDownIcon, MoveUpIcon, PlusIcon, TrashIcon, VideoIcon } from "lucide-react";
import { LearningContent, LearningContentType, LearningContentSubType, LearningType } from "@/features/learnings/service/learningService";
import { Card, CardContent } from "@/shared/ui/card";
import { Switch } from "@radix-ui/react-switch";
import { TextContentInput } from "./blocks/TextContentInput";
import { TitleContentInput } from "./blocks/TitleContentInput";
import { VideoContentInput } from "./blocks/VideoContentInput";
import { useEffect } from "react";

interface Props {
    contents: LearningContent[];
    addContent: (type: LearningContentType, subType: LearningContentSubType) => void;
    updateContent: (index: number, field: keyof LearningContent, value: unknown) => void;
    removeContent: (index: number) => void;
    moveContent: (index: number, direction: 'up' | 'down') => void;
    validationErrors?: Record<string, string>;
    clearFieldError?: (field: string) => void;
    learningType: LearningType;
}


const getContentTypeIcon = (type: string, subType: string) => {
    if (type === 'VIDEO') return <VideoIcon className="h-4 w-4" />;
    if (subType === 'TITLE') return <HeadingIcon className="h-4 w-4" />;
    return <FileTextIcon className="h-4 w-4" />;
};

const getContentTypeName = (type: string, subType: string) => {
    if (type === 'VIDEO') return 'Vídeo';
    if (subType === 'TITLE') return 'Título';
    return 'Texto';
};

const getDefaultContentType = (learningType: LearningType): { type: LearningContentType, subType: LearningContentSubType } => {
    switch (learningType) {
        case 'VIDEO':
        case 'COURSE':
            return { type: 'VIDEO', subType: 'VIDEO' };
        case 'ARTICLE':
        default:
            return { type: 'TEXT', subType: 'CONTENT' };
    }
};

export const ContentBlock: React.FC<Props> = ({
    contents,
    addContent,
    updateContent,
    removeContent,
    moveContent,
    validationErrors = {},
    clearFieldError,
    learningType
}) => {

    useEffect(() => {
        if (contents.length === 0) {
            const { type, subType } = getDefaultContentType(learningType);
            addContent(type, subType);
        } else {
            const { type, subType } = getDefaultContentType(learningType);
            const hasCorrectType = contents.some(content =>
                content.type === type && content.subType === subType
            );

            if (!hasCorrectType) {
                const contentsToRemove = [...contents];
                contentsToRemove.forEach(() => {
                    removeContent(0);
                });
                addContent(type, subType);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [learningType]);

    const canAddVideo = () => {
        const videoCount = contents.filter(content => content.type === 'VIDEO').length;

        if (learningType === 'VIDEO') {
            return videoCount === 0;
        } else if (learningType === 'COURSE') {
            return true;
        }

        return false;
    };

    const handleRemoveContent = (index: number) => {
        removeContent(index);

        const { type, subType } = getDefaultContentType(learningType);
        addContent(type, subType);
    };

    return (
        <Card className="bg-white/[0.03] border-white/[0.06]">
            <CardContent className="pt-6">
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-white">
                            Conteúdo *
                        </h3>

                    </div>

                    {validationErrors.contents && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.contents}</p>
                    )}

                    {contents.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                            <p>Nenhum conteúdo adicionado ainda.</p>
                            <p className="text-sm">
                                {learningType === 'ARTICLE' && 'Use os botões acima para adicionar títulos ou texto.'}
                                {(learningType === 'VIDEO' || learningType === 'COURSE') && 'Use o botão acima para adicionar vídeos.'}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {contents.map((content, index) => {
                                const renderContentComponent = () => {
                                    const validationError = validationErrors[`contents.${index}.value`];

                                    if (content.type === 'VIDEO') {
                                        return (
                                            <VideoContentInput
                                                index={index}
                                                value={content}
                                                onChange={updateContent}
                                                validationError={validationError}
                                                clearFieldError={clearFieldError}
                                            />
                                        );
                                    } else if (content.subType === 'TITLE') {
                                        return (
                                            <TitleContentInput
                                                index={index}
                                                value={content.value}
                                                onChange={updateContent}
                                                validationError={validationError}
                                                clearFieldError={clearFieldError}
                                            />
                                        );
                                    } else {
                                        return (
                                            <TextContentInput
                                                index={index}
                                                value={content.value}
                                                onChange={updateContent}
                                                validationError={validationError}
                                                clearFieldError={clearFieldError}
                                            />
                                        );
                                    }
                                };

                                return (
                                    <div
                                        key={index}
                                        className="bg-[#1A1A1A] border border-[#333333] rounded-lg p-4"
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="flex flex-col gap-1 mt-1">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => moveContent(index, 'up')}
                                                    disabled={index === 0}
                                                    className="h-6 w-6 p-0 text-gray-400 hover:text-white"
                                                >
                                                    <MoveUpIcon className="h-3 w-3" />
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => moveContent(index, 'down')}
                                                    disabled={index === contents.length - 1}
                                                    className="h-6 w-6 p-0 text-gray-400 hover:text-white"
                                                >
                                                    <MoveDownIcon className="h-3 w-3" />
                                                </Button>
                                            </div>

                                            <div className="flex-1 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2 text-white">
                                                        {getContentTypeIcon(content.type, content.subType)}
                                                        <Badge variant="outline" className="text-xs text-white">
                                                            {getContentTypeName(content.type, content.subType)}
                                                        </Badge>
                                                        <span className="text-xs text-white">
                                                            Ordem: {content.order}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Switch
                                                            checked={content.published}
                                                            onCheckedChange={(checked) =>
                                                                updateContent(index, 'published', checked)
                                                            }
                                                            className="scale-75"
                                                        />
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleRemoveContent(index)}
                                                            className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                                            title="Excluir conteúdo"
                                                        >
                                                            <TrashIcon className="h-3 w-3" />
                                                        </Button>
                                                    </div>
                                                </div>
                                                {renderContentComponent()}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                            {canAddVideo() && (
                                <div className="flex justify-center mt-4">
                                    <Button
                                        type="button"
                                        size="sm"
                                        onClick={() => addContent('VIDEO', 'VIDEO')}
                                        className="text-white bg-[#222222] border-[#333333]"
                                    >
                                        <PlusIcon className="h-4 w-4" />
                                        Novo vídeo
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )

}